// Global assignable LFO.
//
// Output is a bipolar signal in roughly [-1, 1] scaled by `depth` (0..1), exposed
// as `output` (a GainNode). The mod router scales that further per destination.
//
// Periodic shapes (sine/tri/square/ramp) use a native OscillatorNode. Sample &
// hold uses a ConstantSourceNode whose offset is fed fresh random steps by a
// lookahead scheduler on the audio clock.

export enum LfoShape {
  Sine = 0,
  Triangle = 1,
  Square = 2,
  Ramp = 3,
  SampleHold = 4,
}

const OSC_TYPE: Record<number, OscillatorType> = {
  [LfoShape.Sine]: 'sine',
  [LfoShape.Triangle]: 'triangle',
  [LfoShape.Square]: 'square',
  [LfoShape.Ramp]: 'sawtooth',
};

/** Deterministic LFO sample in [-1, 1] for a normalized phase in [0, 1). */
export function lfoValue(shape: LfoShape, phase: number): number {
  const p = phase - Math.floor(phase);
  switch (shape) {
    case LfoShape.Sine:
      return Math.sin(2 * Math.PI * p);
    case LfoShape.Triangle:
      return 4 * Math.abs(p - 0.5) - 1;
    case LfoShape.Square:
      return p < 0.5 ? 1 : -1;
    case LfoShape.Ramp:
      return 2 * p - 1;
    default:
      return 0; // S&H is stochastic; handled separately
  }
}

export class LFO {
  readonly output: GainNode;
  private readonly ctx: BaseAudioContext;

  private osc: OscillatorNode | null = null;
  private sh: ConstantSourceNode | null = null;
  private shTimer: ReturnType<typeof setInterval> | null = null;
  private shNextStep = 0;

  private shape: LfoShape = LfoShape.Sine;
  private rate = 5;
  private depth = 0;

  constructor(ctx: BaseAudioContext) {
    this.ctx = ctx;
    this.output = ctx.createGain();
    this.output.gain.value = this.depth;
  }

  start(): void {
    this.rebuildSource();
  }

  setRate(hz: number): void {
    this.rate = Math.max(0.001, hz);
    if (this.osc) this.osc.frequency.setValueAtTime(this.rate, this.ctx.currentTime);
    // S&H step cadence is read live by the scheduler.
  }

  setDepth(depth: number): void {
    this.depth = Math.max(0, Math.min(1, depth));
    this.output.gain.setTargetAtTime(this.depth, this.ctx.currentTime, 0.01);
  }

  setShape(shape: LfoShape): void {
    if (shape === this.shape) return;
    this.shape = shape;
    this.rebuildSource();
  }

  private rebuildSource(): void {
    this.teardownSources();
    if (this.shape === LfoShape.SampleHold) {
      this.buildSampleHold();
    } else {
      this.buildOscillator();
    }
  }

  private buildOscillator(): void {
    const osc = this.ctx.createOscillator();
    osc.type = OSC_TYPE[this.shape] ?? 'sine';
    osc.frequency.value = this.rate;
    osc.connect(this.output);
    osc.start();
    this.osc = osc;
  }

  private buildSampleHold(): void {
    const src = this.ctx.createConstantSource();
    src.offset.value = Math.random() * 2 - 1;
    src.connect(this.output);
    src.start();
    this.sh = src;
    this.shNextStep = this.ctx.currentTime;
    // Lookahead: schedule random steps ~200ms ahead, refilled every 100ms.
    this.shTimer = setInterval(() => this.fillSampleHold(), 100);
    this.fillSampleHold();
  }

  private fillSampleHold(): void {
    if (!this.sh) return;
    const horizon = this.ctx.currentTime + 0.2;
    const period = 1 / this.rate;
    while (this.shNextStep < horizon) {
      const value = Math.random() * 2 - 1;
      this.sh.offset.setValueAtTime(
        value,
        Math.max(this.shNextStep, this.ctx.currentTime),
      );
      this.shNextStep += period;
    }
  }

  private teardownSources(): void {
    if (this.shTimer !== null) {
      clearInterval(this.shTimer);
      this.shTimer = null;
    }
    if (this.osc) {
      try {
        this.osc.stop();
      } catch {
        /* not started */
      }
      this.osc.disconnect();
      this.osc = null;
    }
    if (this.sh) {
      try {
        this.sh.stop();
      } catch {
        /* not started */
      }
      this.sh.disconnect();
      this.sh = null;
    }
  }

  dispose(): void {
    this.teardownSources();
    this.output.disconnect();
  }
}
